import { useSession } from 'next-auth/react';
import { isAdminRole } from '../lib/role';

/**
 * Hook for building dynamic URLs based on user role (admin vs non-admin)
 * Provides utilities for URL construction with appropriate prefixes
 *
 * @usage
 * This hook is used extensively across the application to build URLs that are prefixed with '/admin' for admin users.
 * It is primarily used for navigation links and programmatic redirects to ensure users are sent to the correct version of a page.
 *
 * It can be found in components like:
 * - src/components/common/TopNavbar.tsx
 * - src/components/student-management/StudentActions.tsx
 * - src/components/dashboard/SchoolHeader.tsx
 * And in page files like:
 * - src/app/reports/page.tsx
 * - src/app/grades/page.tsx
 * - src/app/team/layout.tsx
 * - src/app/student-management/...
 * - src/app/fees/...
 */
export const useRoleBasedUrl = () => {
  const { data: session } = useSession();

  // Get user role from session
  const userRole = session?.user?.role || '';
  const isAdmin = isAdminRole(userRole);

  /**
   * Build URL based on user role
   * @param baseRoute - The base route without prefix (e.g., 'student-management', 'dashboard')
   * @returns string - Complete URL with appropriate prefix
   */
  const buildUrl = (baseRoute: string): string => {
    // Remove leading slash if present
    const cleanRoute = baseRoute.startsWith('/')
      ? baseRoute.slice(1)
      : baseRoute;

    // Add admin prefix for admin users
    const prefix = isAdmin ? '/admin' : '';

    return `${prefix}/${cleanRoute}`;
  };

  /**
   * Get route prefix based on user role
   * @returns string - Empty string for non-admin, '/admin' for admin
   */
  const getRoutePrefix = (): string => {
    return isAdmin ? '/admin' : '';
  };

  /**
   * Check if a given URL matches the user's role context
   * @param url - URL to check
   * @returns boolean - True if URL matches user's expected context
   */
  const isUrlValidForUser = (url: string): boolean => {
    const hasAdminPrefix = url.startsWith('/admin');
    return isAdmin ? hasAdminPrefix : !hasAdminPrefix;
  };

  /**
   * Convert an existing URL to the appropriate format for current user
   * @param url - URL to convert
   * @returns string - URL adjusted for current user's role
   */
  const normalizeUrl = (url: string): string => {
    // Remove admin prefix if present
    const cleanUrl = url.startsWith('/admin/')
      ? url.replace('/admin/', '')
      : url.replace('/admin', '');
    const baseRoute = cleanUrl.startsWith('/') ? cleanUrl.slice(1) : cleanUrl;

    return buildUrl(baseRoute);
  };

  return {
    buildUrl,
    getRoutePrefix,
    isUrlValidForUser,
    normalizeUrl,
    isAdmin,
    userRole,
  };
};
